/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import moment from "moment";
import { FormattedMessage, IntlShape } from "react-intl";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import { ReactComponent as ShipmentExceptionIcon } from "assets/images/iconsV2/shipment-exception.svg";
import getLabel from "helpers/getLabel";
import Badge from "components/atoms/Badge/Badge";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import { formatDatetime, formatWeight, roundTo } from "helpers/commonHelpers";
import Cookies from "js-cookie";
import { ShipmentStatusColors, ShipmentTypes } from "constants/";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";

function formatDates(date: any, includeTime: boolean = false) {
  if (date === "ASAP") return "ASAP";
  if (includeTime) return moment(date).format("DD MMM YYYY HH:mm");
  return moment(date).format("DD MMM YYYY");
}

const getCommonShipmentColumns = (
  intl: IntlShape,
  user_type: any,
  can_consolidate_orders: boolean,
  can_create_multidrop_quote_requests: boolean,
) => {
  const columns: Column[] = [
    {
      id: "shipment",
      Header: intl.formatMessage({
        id: "general.shipment",
        defaultMessage: "Shipment",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "shipment_reference",
      sortable: true,
      Cell: ({ value, row }: any) => (
        <>
          <p className="font-b4 d-flex align-items-center mb-1">
            {value}
            {row.original?.is_off_platform_shipment && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="shipment-tip">
                    <span className="font-weight-normal">
                      <FormattedMessage id="general.offPlatformShipment" defaultMessage="Shipment not booked on Cargobase" />
                    </span>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} className="font-12 text-primary ml-2" />
              </OverlayTrigger>
            )}
          </p>
          <span>{row.original?.shipment_title}</span>
        </>
      ),
    },
    {
      Header: intl.formatMessage({
        id: "general.status",
        defaultMessage: "Status",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "status",
      sortable: true,
      Cell: ({ value, row }: any) => {
        const hasException = row.original?.has_exception;
        return (
          <div className="d-flex align-items-center gap-md">
            {value ? (
              <div className="d-flex flex-column align-items-start">
                <Badge type={ShipmentStatusColors[value] || "secondary"} label={value} />
              </div>
            ) : (
              "-"
            )}
            {hasException && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="shipment-tip">
                    <span className="font-weight-normal">
                      <FormattedMessage id="general.exception" defaultMessage="Exception" />
                    </span>
                  </Tooltip>
                }
              >
                <ShipmentExceptionIcon />
              </OverlayTrigger>
            )}
          </div>
        );
      },
    },
    ...(user_type === "shipper" && (can_consolidate_orders || can_create_multidrop_quote_requests)
      ? [
          {
            id: "type",
            Header: intl.formatMessage({
              id: "general.type",
              defaultMessage: "Type",
            }),
            group: intl.formatMessage({
              id: "columns.basic",
              defaultMessage: "Basic",
            }),
            accessor: "shipment_type",
            sortable: true,
            Cell: ({ value }: { value: number }) => {
              const typeMessages: Record<number, { id: string; defaultMessage: string }> = {
                [ShipmentTypes.CONSOLIDATED]: { id: "quote.listing.typeConsolidated", defaultMessage: "Consolidated" },
                [ShipmentTypes.MULTIDROP]: { id: "quote.listing.typeMultiDrop", defaultMessage: "Multi-drop" },
                [ShipmentTypes.REGULAR]: { id: "quote.listing.typeSingle", defaultMessage: "Single" },
              };
              const message = typeMessages[value] ?? typeMessages[ShipmentTypes.REGULAR];
              const type = intl.formatMessage(message);
              return <span className="font-b5">{type}</span>;
            },
          },
        ]
      : []),
    {
      Header: intl.formatMessage({
        id: "shipments.freightModeTableHeader",
        defaultMessage: "Freight Mode",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "freight_mode",
      Cell: ({ value, row }: any) => {
        const Icon = getFreightModeIcon(value, row.original?.freight_mode_type, true);
        return (
          <div>
            {value ? (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="freight-mode">
                    <span className="font-weight-normal">{row.original?.freight_mode}</span>
                  </Tooltip>
                }
              >
                {value && <Icon />}
              </OverlayTrigger>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      id: "route",
      Header: intl.formatMessage({
        id: "quote.listing.fromTo",
        defaultMessage: "From-to",
      }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "pickup_city",
      sortable: true,
      Cell: ({ row }: any) => {
        const deliveryDate =
          row.original?.schedule_dates_no_conversion?.updated_delivery_datetime ||
          row.original?.schedule_dates_no_conversion?.quoted_delivery_datetime ||
          row.original?.updated_delivery_datetime ||
          row.original?.quoted_delivery_datetime;
        const pickupDate =
          row.original?.schedule_dates_no_conversion?.updated_pickup_datetime ||
          row.original?.schedule_dates_no_conversion?.quoted_pickup_datetime ||
          row.original?.updated_pickup_datetime ||
          row.original?.quoted_pickup_datetime;
        const pickupCity = row.original?.pickup_city;
        const deliveryCity = row.original?.stop1_city;
        const isOffPlatform = row.original?.is_off_platform_shipment;
        return (
          <>
            <div>
              <div className="d-flex justify-content-between w-190-px">
                <div className="font-b5 overflow-hidden text-truncate text-secondary w-90-px">
                  <span>{pickupCity}</span>
                  {!isOffPlatform && (
                    <div className="text-primary text-truncate">
                      {Array.isArray(pickupDate) ? (
                        <OverlayTrigger
                          placement="top"
                          delay={{ show: 50, hide: 50 }}
                          overlay={
                            <Tooltip id="button-tooltip">
                              {formatDatetime(pickupDate[0])} - {formatDatetime(pickupDate[1])}
                            </Tooltip>
                          }
                        >
                          <span>
                            {formatDatetime(pickupDate[0])} - {formatDatetime(pickupDate[1])}
                          </span>
                        </OverlayTrigger>
                      ) : (
                        formatDatetime(pickupDate)
                      )}
                    </div>
                  )}
                </div>
                <div className="font-b5 text-right overflow-hidden text-truncate text-secondary w-90-px">
                  <span>{deliveryCity}</span>
                  {!isOffPlatform && <div className="font-b5 text-primary">{formatDatetime(deliveryDate)}</div>}
                </div>
              </div>
            </div>
            {Cookies.get("listing_sizeperpage") !== "100" && (
              <div className="d-flex justify-content-between position-relative w-190-px transit-line">
                <div
                  style={{
                    width: 190,
                    height: 2,
                    borderBottom: `2px dotted black`,
                    position: "absolute",
                    top: 3,
                  }}
                />
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "2px solid black",
                      zIndex: 10,
                    }}
                  />
                </div>

                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "2px solid black",
                      zIndex: 10,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        );
      },
    },
    {
      Header: intl.formatMessage({ id: "new-request.packing-list.dangerousGoods", defaultMessage: "Dangerous goods" }),
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      accessor: "dangerous_goods",
      Cell: ({ value }) => <div>{value || "No"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.totalWeightKg", defaultMessage: "Total weight (kg)" }),
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      accessor: "total_weight_kg",
      Cell: ({ value }) => <div>{formatWeight(value, "kg")}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.totalWeightLbs", defaultMessage: "Total weight (lbs)" }),
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      accessor: "total_weight_lbs",
      Cell: ({ value }) => <div>{formatWeight(value, "lbs")}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.totalVolumeCbm", defaultMessage: "Total volume (cbm)" }),
      group: intl.formatMessage({
        id: "columns.goodsInformation",
        defaultMessage: "Goods information",
      }),
      accessor: "total_volume_cbm",
      Cell: ({ value }) => (
        <div>
          {value || value === 0 ? (
            <span>
              {roundTo(value, 4)}m<sup>3</sup>
            </span>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.exportClearance", defaultMessage: "Export clearance" }),
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      accessor: "export_clearance",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "quote-request-detail.transitClearance", defaultMessage: "Transit clearance" }),
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      accessor: "transit_clearance",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "quote-request-detail.importClearance", defaultMessage: "Import clearance" }),
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      accessor: "import_clearance",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "new-request.service.serviceRequirement", defaultMessage: "Service requirement" }),
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      accessor: "service_requirement",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "new-request.service.incoterms", defaultMessage: "Incoterms" }),
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      accessor: "incoterms",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.primaryTracking", defaultMessage: "Primary Tracking" }),
      group: intl.formatMessage({
        id: "columns.tracking",
        defaultMessage: "Tracking",
      }),
      accessor: "primary_tracking",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.trackingNumberAdded", defaultMessage: "Tracking number added" }),
      group: intl.formatMessage({
        id: "columns.tracking",
        defaultMessage: "Tracking",
      }),
      accessor: "tracking_number_added",
      Cell: ({ value }) => (
        <div>
          {value === 1 ? (
            <FormattedMessage id="general.yes" defaultMessage="Yes" />
          ) : (
            <FormattedMessage id="general.no" defaultMessage="No" />
          )}
        </div>
      ),
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.MBLNumber", defaultMessage: "MBL Number" }),
      group: intl.formatMessage({
        id: "columns.tracking",
        defaultMessage: "Tracking",
      }),
      accessor: "mbl_number",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.containerNumber", defaultMessage: "Container Number" }),
      group: intl.formatMessage({
        id: "columns.tracking",
        defaultMessage: "Tracking",
      }),
      accessor: "container_number",
      Cell: ({ value }) => <div>{value?.replaceAll("||", "") || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.MAWBNumber", defaultMessage: "MAWB Number" }),
      group: intl.formatMessage({
        id: "columns.tracking",
        defaultMessage: "Tracking",
      }),
      accessor: "mawb_tracking_reference",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.OtherTracking", defaultMessage: "Other Tracking" }),
      group: intl.formatMessage({
        id: "columns.tracking",
        defaultMessage: "Tracking",
      }),
      accessor: "other_tracking_reference",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.TruckNumber", defaultMessage: "Truck Number" }),
      group: intl.formatMessage({
        id: "columns.tracking",
        defaultMessage: "Tracking",
      }),
      accessor: "truck_tracking_reference",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.createdDate", defaultMessage: "Created date" }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "created",
      Cell: ({ value }) => <div>{formatDates(value, true) || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.contracted", defaultMessage: "Contracted" }),
      group: intl.formatMessage({
        id: "columns.basic",
        defaultMessage: "Basic",
      }),
      accessor: "contracted",
      Cell: ({ value }) => <div>{value ? "Yes" : "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.createdBy", defaultMessage: "Created by" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "created_by",
      Cell: ({ value }) => <div>{value.name || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.fromCity", defaultMessage: "From city" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "pickup_city",
      sortable: true,
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.toCity", defaultMessage: "To city" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "stop1_city",
      sortable: true,
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "general.insurance", defaultMessage: "Insurance" }),
      group: intl.formatMessage({
        id: "columns.service",
        defaultMessage: "Service",
      }),
      accessor: "insurance",
      Cell: ({ value }) => <div>{value ? "True" : "False"}</div>,
    },
    {
      Header: getLabel("Reason code", "capitalize", "", false, true),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "reason_code",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: getLabel("Cost Center / WBS / Project #", "capitalize", "", false, true),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "cost_center",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.polETA", defaultMessage: "POL ETA" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "pol_eta",
      Cell: ({ value }) => <div>{value ? formatDate(value, DateTimeFormat.fullDateTimeFormat) : "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.polLocode", defaultMessage: "POL Locode" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "pol_locode",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.podETA", defaultMessage: "POD ETA" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "pod_eta",
      Cell: ({ value }) => <div>{value ? formatDate(value, DateTimeFormat.fullDateTimeFormat) : "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.podTime", defaultMessage: "POD Time" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "pod_time",
      Cell: ({ value }) => <div>{value ? formatDate(value, DateTimeFormat.fullDateTimeFormat) : "-"}</div>,
    },
    {
      Header: intl.formatMessage({ id: "tableHeaders.podLocode", defaultMessage: "POD Locode" }),
      group: intl.formatMessage({
        id: "columns.other",
        defaultMessage: "Other",
      }),
      accessor: "pod_locode",
      Cell: ({ value }) => <div>{value || "-"}</div>,
    },
  ];

  return columns;
};

export default getCommonShipmentColumns;
